import * as THREE from 'three';
import type { Atlas } from '../textures.ts';
import { MeshBuilder } from './build.ts';

/**
 * An MR-63 consist: the blue-and-white stock that ran on the Montreal metro from
 * 1966 until 2018. Chosen over the newer Azur because its flat slab sides suit a
 * few hundred triangles far better than an articulated open-gangway train would.
 *
 * A consist, not a single unit - a cab car at each end with middle cars between.
 *
 * Each train is exactly three meshes: every car body merged into one, and the
 * sliding doors merged into one mesh per direction of travel. A mesh per door
 * leaf is the obvious approach and would cost 18 draw calls per train, which on
 * this hardware matters far more than the geometry does.
 */

const CAR_LENGTH = 11;
const CAR_GAP = 0.7;
const CAR_WIDTH = 2.6;
const CAR_HEIGHT = 3.2;
const FLOOR_Y = 0.35; // sits on the roll ways
/**
 * Doors. The numbers here are related, not independent, and getting that wrong
 * is what made the earlier ones look broken: each leaf was as wide as the whole
 * doorway should have been, and the travel was shorter than a leaf, so open
 * doors left a hole wider than the doorway with the leaves still half across it.
 *
 * The relationship that has to hold: a leaf is half the doorway, and it travels
 * exactly its own width, so when open the pair has fully cleared the opening and
 * sits either side of it.
 */
const DOORWAY_WIDTH = 1.6;
const LEAF_WIDTH = DOORWAY_WIDTH / 2;
const DOOR_TRAVEL = LEAF_WIDTH;

/**
 * Door height is derived from the car, not chosen. A fixed 2.0 on a 3.2-tall
 * body left a third of the car side blank above the doorway, and worse, put the
 * door's head partway up the car's own window band - the doors read as
 * undersized because they visibly disagreed with the windows beside them.
 *
 * Sill just above the floor, head just below the roof trim, which puts the top
 * of the door level with the top of the window band.
 */
const DOOR_SILL = 0.05;
const DOOR_HEAD_GAP = 0.55;
const DOOR_HEIGHT = CAR_HEIGHT - DOOR_SILL - DOOR_HEAD_GAP;
const DOOR_THICKNESS = 0.08;

/**
 * Standoffs from the car's side, in layers: the dark doorway panel first, then
 * the leaves in front of it. These are not styling. At a hair's clearance the
 * surfaces z-fight and which one wins flickers as the camera turns, so the doors
 * appear to double and tear. Keep each gap well above the depth buffer's
 * resolution over the scene.
 */
const DOORWAY_STANDOFF = 0.05;
const DOORWAY_THICKNESS = 0.06;
const DOOR_STANDOFF = 0.16;

/** Doorway centres along a car, as offsets from its middle. */
const DOOR_OFFSETS = [-3.4, 0, 3.4];

export interface TrainOptions {
  cars?: number;
  /** Centre line of the track this train sits on. */
  trackZ: number;
  /**
   * Which way the doors face: -1 for a platform on the -Z side of the track,
   * +1 for +Z. Doors are only built on that side; the far side is never seen.
   */
  doorsFacing: -1 | 1;
}

export interface Train {
  group: THREE.Group;
  /** Total length, used to park it and to drive it off both ends. */
  length: number;
  /** 0 = shut, 1 = fully open. */
  setDoors(open: number): void;
}

export function createTrain(
  atlas: Atlas,
  material: THREE.Material,
  options: TrainOptions,
): Train {
  const { cars = 4, trackZ, doorsFacing } = options;
  const group = new THREE.Group();

  const bodies = new MeshBuilder();
  // One builder per slide direction, so all leaves moving the same way share a
  // mesh and can be translated together.
  const leaves = [new MeshBuilder(), new MeshBuilder()];

  const totalLength = cars * CAR_LENGTH + (cars - 1) * CAR_GAP;
  const firstCenter = -totalLength / 2 + CAR_LENGTH / 2;
  const side = CAR_WIDTH / 2;
  const doorwayZ = trackZ +
    doorsFacing * (side + DOORWAY_STANDOFF + DOORWAY_THICKNESS / 2);
  const doorZ = trackZ + doorsFacing * (side + DOOR_STANDOFF + DOOR_THICKNESS / 2);
  const doorY = FLOOR_Y + DOOR_SILL + DOOR_HEIGHT / 2;

  for (let i = 0; i < cars; i++) {
    const centerX = firstCenter + i * (CAR_LENGTH + CAR_GAP);
    const isCab = i === 0 || i === cars - 1;
    // The windscreen faces outward at each end of the consist.
    const cabFacing = i === 0 ? -1 : 1;
    addCar(bodies, atlas, centerX, trackZ, isCab, cabFacing);

    for (const offset of DOOR_OFFSETS) {
      // A dark panel behind the leaves, so an open door shows an opening rather
      // than more of the car's own painted side.
      bodies.box(
        [centerX + offset, doorY, doorwayZ],
        [DOORWAY_WIDTH, DOOR_HEIGHT, DOORWAY_THICKNESS],
        { all: atlas.tile('dark') },
        0x6a6a6a,
      );

      for (let leaf = 0; leaf < 2; leaf++) {
        const direction = leaf === 0 ? -1 : 1;
        // Closed, the two leaves meet on the doorway's centre line.
        leaves[leaf].box(
          [centerX + offset + (direction * LEAF_WIDTH) / 2, doorY, doorZ],
          [LEAF_WIDTH, DOOR_HEIGHT, DOOR_THICKNESS],
          { all: atlas.tile('trainDoor') },
        );
      }
    }
  }

  const bodyMesh = mesh(bodies.build(), material);
  const leafMeshes = leaves.map((builder) => mesh(builder.build(), material));
  group.add(bodyMesh, ...leafMeshes);

  function setDoors(open: number) {
    const t = Math.min(Math.max(open, 0), 1);
    leafMeshes[0].position.x = -DOOR_TRAVEL * t;
    leafMeshes[1].position.x = DOOR_TRAVEL * t;
  }
  setDoors(0);

  return { group, length: totalLength, setDoors };
}

function addCar(
  b: MeshBuilder,
  atlas: Atlas,
  centerX: number,
  z: number,
  isCab: boolean,
  cabFacing: number,
) {
  const bodyY = FLOOR_Y + CAR_HEIGHT / 2;
  const side = atlas.tile('trainSide');
  const front = atlas.tile('trainFront');

  // Main body. One tile covers 3 world units along the side, so a car reads as a
  // row of window bays rather than one stretched image.
  b.box(
    [centerX, bodyY, z],
    [CAR_LENGTH, CAR_HEIGHT, CAR_WIDTH],
    {
      sides: side,
      px: isCab && cabFacing > 0 ? front : side,
      nx: isCab && cabFacing < 0 ? front : side,
      py: atlas.tile('trainRoof'),
      ny: atlas.tile('dark'),
    },
    0xffffff,
    3,
  );

  // Roof gear.
  b.box(
    [centerX, FLOOR_Y + CAR_HEIGHT + 0.15, z],
    [CAR_LENGTH * 0.55, 0.3, CAR_WIDTH * 0.5],
    { all: atlas.tile('trainRoof') },
    0xc8c8c4,
  );

  // Skirt below the body, hiding the running gear.
  b.box(
    [centerX, FLOOR_Y * 0.5, z],
    [CAR_LENGTH - 0.4, FLOOR_Y, CAR_WIDTH - 0.25],
    { all: atlas.tile('dark') },
    0xbdbdb8,
  );

  // Rubber tyres, the Montreal signature.
  for (const dx of [-CAR_LENGTH / 2 + 2.2, CAR_LENGTH / 2 - 2.2]) {
    for (const dz of [-1, 1]) {
      b.box(
        [centerX + dx, 0.42, z + dz * (CAR_WIDTH / 2 - 0.35)],
        [1.5, 0.84, 0.5],
        { all: atlas.tile('dark') },
        0x8a8a86,
      );
    }
  }
}

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Mesh {
  const m = new THREE.Mesh(geometry, material);
  // The diorama is always fully in view; per-object culling only costs matrix
  // maths and bounding-sphere tests we do not need.
  m.frustumCulled = false;
  return m;
}
